import React, { useMemo, useState } from "react";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { Wrench, Mail, HardDrive, MessageCircle, Server, Layers } from "lucide-react";
import dayjs from "dayjs";

import {
  Input,
  Label,
  Button,
  Card,
  CardContent,
} from "components/ui";
import {
  SubmitLoaderButton,
  VisiblitySwitch,
  InputTextarea,
  CustomTimePicker,
} from "components/atoms";
import CustomDatePicker from "components/atoms/CustomDatePicker";
import { closeAdminModal } from "store/slices/common/adminModal.slice";
import { AddMaintenanceAction, UpdateMaintenanceAction } from "store/middlewares/settings/settings";

/* ------------------------------------------------------------------ */
/* Platforms                                                           */
/* ------------------------------------------------------------------ */
const PLATFORMS = [
  {
    key: "mail",
    name: "Mail",
    description: "Notify users via system email.",
    icon: Mail,
  },
  {
    key: "drive",
    name: "Drive",
    description: "Notify users via system email.",
    icon: HardDrive,
  },
  {
    key: "chat",
    name: "Chat",
    description: "Notify users via system email.",
    icon: MessageCircle,
  },
  {
    key: "service",
    name: "Service",
    description: "Notify users via system email.",
    icon: Server,
  },
  {
    key: "middleware",
    name: "Middleware",
    description: "Notify users via system email.",
    icon: Layers,
  },
];

const MIN_START_LEAD_MINUTES = 8 * 60 + 15; // 8 hours 15 minutes

/**
 * Earliest allowed start = now + 8h 15m, truncated to the minute
 * (pickers have no seconds, so the exact minute must be selectable).
 */
const getMinStart = () =>
  dayjs().add(MIN_START_LEAD_MINUTES, "minute").startOf("minute");

/** Parse API timestamp (string/number epoch ms or ISO) → dayjs | null */
const parseTs = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  const d = Number.isFinite(n) ? dayjs(n) : dayjs(value);
  return d.isValid() ? d : null;
};

/**
 * Combine date (dayjs) + time (dayjs | "HH:mm" string) → dayjs | null
 */
const combineDateTime = (date, time) => {
  if (!date) return null;
  const datePart = dayjs(date);
  if (!datePart.isValid()) return null;

  let timePart = null;
  if (time == null || time === "") {
    timePart = null;
  } else if (dayjs.isDayjs(time)) {
    timePart = time.isValid() ? time : null;
  } else if (typeof time === "string") {
    const t = dayjs(time, ["HH:mm", "HH:mm:ss", "hh:mm A", "hh:mm:ss A"], true);
    timePart = t.isValid() ? t : dayjs(time);
    if (!timePart.isValid()) timePart = null;
  } else {
    timePart = dayjs(time);
    if (!timePart.isValid()) timePart = null;
  }

  if (!timePart) return null;

  return dayjs(
    `${datePart.format("YYYY-MM-DD")} ${timePart.format("HH:mm")}`,
  );
};

/** Combine → timestamp for payload */
const toTimestamp = (date, time) => {
  const d = combineDateTime(date, time);
  return d && d.isValid() ? d.valueOf() : null;
};

/** platforms from API: [{ platform: "drive" }] or ["drive"] or "all" */
const getInitialPlatforms = (row) => {
  const raw = row?.platforms ?? row?.platform;
  if (!raw) {
    return { mail: true, drive: true, chat: true };
  }

  let keys = [];
  if (Array.isArray(raw)) {
    keys = raw
      .map((p) => (typeof p === "string" ? p : p?.platform))
      .filter(Boolean);
  } else if (typeof raw === "string") {
    keys = raw === "all" ? ["mail", "drive", "chat"] : [raw];
  }

  if (keys.includes("all") || keys.length === 3) {
    return { mail: true, drive: true, chat: true };
  }

  return {
    mail: keys.includes("mail"),
    drive: keys.includes("drive"),
    chat: keys.includes("chat"),
  };
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return dayjs(a).isValid() && dayjs(b).isValid() && dayjs(a).isSame(dayjs(b), "day");
};

/* ------------------------------------------------------------------ */
/* Platform card (only the switch is interactive)                      */
/* ------------------------------------------------------------------ */
const PlatformCard = ({ platform, enabled, onToggle }) => {
  return (
    <Card
      className={`relative overflow-hidden transition-all duration-300 ${enabled ? "shadow-md border-blue-200" : "bg-gray-50 opacity-70 shadow-sm"
        }`}
    >
      <CardContent className="flex flex-col gap-1 pt-3 pb-3">
        <div className="flex items-start justify-between">
          <h3
            className={`text-sm font-semibold transition-colors ${enabled ? "text-foreground" : "text-gray-500"
              }`}
          >
            {platform.name}
          </h3>
          <VisiblitySwitch
            checked={enabled}
            onChange={(checked) => onToggle(checked)}
          />
        </div>
        <div>
          <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
            {platform.description}
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-xs font-medium">
          <span
            className={`size-1.5 rounded-full ${enabled ? "bg-green-500" : "bg-gray-300"
              }`}
          />
          <span className={enabled ? "text-green-700" : "text-muted-foreground"}>
            {enabled ? "Included" : "Excluded"}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/* Modal                                                               */
/* ------------------------------------------------------------------ */
function ModalEditMaintenance({ row, filter }) {
  const dispatch = useDispatch();
  const [formError, setFormError] = useState(false);

  // Prefer API fields: starts_at / ends_at (epoch ms string)
  const startTs = parseTs(row?.starts_at ?? row?.startAt ?? row?.start_at);
  const endTs = parseTs(row?.ends_at ?? row?.endAt ?? row?.end_at);

  const formik = useFormik({
    initialValues: {
      title: row?.title?.trim() ? row.title.trim() : "",
      description: row?.description?.trim()
        ? row.description.trim()
        : row?.reason?.trim()
          ? row.reason.trim()
          : "",
      startDate: startTs,
      startTime: startTs,
      endDate: endTs,
      endTime: endTs,
      platforms: getInitialPlatforms(row),
      sendEmail: row?.send_email ?? row?.sendEmail ?? true,
      sendNotification: row?.send_notification ?? row?.sendNotification ?? true,
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .trim()
        .required("Title is required")
        .max(80, "Max 80 characters"),
      description: Yup.string().trim().required("Description is required"),

      // "required" only fires when empty; "invalid" fires when a value exists but is bad
      startDate: Yup.mixed()
        .nullable()
        .required("Start date is required")
        .test("start-date-valid", "Start date is invalid", (value) =>
          value == null ? true : dayjs(value).isValid(),
        ),
      startTime: Yup.mixed()
        .nullable()
        .required("Start time is required")
        .test("start-time-valid", "Start time is invalid", (value) =>
          value == null ? true : dayjs(value).isValid(),
        )
        .test(
          "start-at-least-8h-15m",
          "Start time is invalid: must be at least 8 hours 15 minutes from now",
          function (value) {
            const { startDate } = this.parent;
            const start = combineDateTime(startDate, value);
            if (!start || !start.isValid()) return true;
            return !start.isBefore(getMinStart());
          },
        ),
      endDate: Yup.mixed()
        .nullable()
        .required("End date is required")
        .test("end-date-valid", "End date is invalid", (value) =>
          value == null ? true : dayjs(value).isValid(),
        ),
      endTime: Yup.mixed()
        .nullable()
        .required("End time is required")
        .test("end-time-valid", "End time is invalid", (value) =>
          value == null ? true : dayjs(value).isValid(),
        )
        .test(
          "end-at-least-15-min-after-start",
          "End time is invalid: must be at least 15 minutes after start",
          function (value) {
            const { startDate, startTime, endDate } = this.parent;
            const start = combineDateTime(startDate, startTime);
            const end = combineDateTime(endDate, value);
            if (!start || !end) return true;
            return !end.isBefore(start.add(15, "minute"));
          },
        ),

      platforms: Yup.mixed().test(
        "one-platform-on",
        "Select at least one platform",
        (value) => Object.values(value || {}).some(Boolean),
      ),
    }),
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting }) => {
      const selected = PLATFORMS.filter((p) => values.platforms[p.key]).map(
        (p) => p.key,
      );
      const platform =
        selected.length === PLATFORMS.length ? ["all"] : selected;

      const payload = {
        title: values.title.trim(),
        description: values.description.trim(),
        startAt: toTimestamp(values.startDate, values.startTime),
        endAt: toTimestamp(values.endDate, values.endTime),
        sendEmail: Boolean(values.sendEmail),
        sendNotification: Boolean(values.sendNotification),
        platform,
      };

      if (row) {
        dispatch(
          UpdateMaintenanceAction({
            id: row.id,
            ...payload,
            filter,
            onSuccess: () => {
              setSubmitting(false);
              dispatch(closeAdminModal());
            },
            onError: () => {
              setFormError(true);
              setSubmitting(false);
            },
          }),
        );
      } else {
        dispatch(
          AddMaintenanceAction({
            ...payload,
            filter,
            onSuccess: () => {
              setSubmitting(false);
              dispatch(closeAdminModal());
            },
            onError: () => {
              setFormError(true);
              setSubmitting(false);
            },
          }),
        );
      }
    },
  });

  const activePlatforms = PLATFORMS.filter(
    (p) => formik.values.platforms[p.key],
  );

  const today = dayjs().startOf("day");
  const minStartMoment = getMinStart();

  // Earliest selectable start date: today if now+8h15m is still today, otherwise tomorrow
  const startDateMin = minStartMoment.startOf("day");

  // Start time is restricted only when the selected date is the same day as now+8h15m
  const startTimeMin =
    formik.values.startDate && isSameDay(formik.values.startDate, minStartMoment)
      ? minStartMoment
      : null;

  const endDateMin = formik.values.startDate
    ? dayjs(formik.values.startDate).startOf("day")
    : today;

  // End time min when same day as start → start + 15 min
  const endTimeMin = useMemo(() => {
    const start = combineDateTime(
      formik.values.startDate,
      formik.values.startTime,
    );
    if (!start || !start.isValid()) return null;
    if (
      formik.values.endDate &&
      isSameDay(formik.values.endDate, formik.values.startDate)
    ) {
      return start.add(15, "minute");
    }
    return null;
  }, [formik.values.startDate, formik.values.startTime, formik.values.endDate]);

  // Handlers keep what the user entered; the schema reports "invalid" instead of clearing to "required"
  const handleStartDateChange = (newValue) => {
    formik.setFieldValue("startDate", newValue);

    // Keep end date from sitting before start date
    if (
      newValue &&
      formik.values.endDate &&
      dayjs(formik.values.endDate).isBefore(dayjs(newValue), "day")
    ) {
      formik.setFieldValue("endDate", newValue);
    }
  };

  const handleStartTimeChange = (newValue) => {
    formik.setFieldValue("startTime", newValue);
  };

  const handleEndDateChange = (newValue) => {
    formik.setFieldValue("endDate", newValue);
  };

  const handleEndTimeChange = (newValue) => {
    formik.setFieldValue("endTime", newValue);
  };

  // Revalidate on submit and touch every field so all errors display (needed on edit)
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const errors = await formik.validateForm();

    if (Object.keys(errors).length > 0) {
      formik.setTouched(
        {
          title: true,
          description: true,
          startDate: true,
          startTime: true,
          endDate: true,
          endTime: true,
          platforms: true,
        },
        false,
      );
      return;
    }

    formik.handleSubmit();
  };

  return (
    <form
      onSubmit={handleFormSubmit}
      className="grid gap-4"
    >
      {/* Title with icon */}
      <div className="flex items-center gap-3.5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
          <Wrench className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          {row ? "Edit maintenance" : "Add maintenance"}
        </h2>
      </div>

      {/* Title */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          name="title"
          type="text"
          variant="lg"
          maxLength={80}
          placeholder="Maintenance title"
          className="w-full"
          value={formik.values.title}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.title && formik.errors.title}
          errorMsg={formik.errors.title}
          isSpace={true}
          blockHtmlTags={true}
        />
      </div>

      {/* Description */}
      <div className="flex flex-col gap-3">
        <Label htmlFor="description">Description</Label>
        <InputTextarea
          id="description"
          name="description"
          placeholder="Describe the maintenance window..."
          textareaClassName="rounded-lg border border-solid w-full px-2 py-2 no-scrollbar min-h-[80px]"
          className="!py-0"
          maxLength={255}
          isRequired={false}
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.description && formik.errors.description}
          errorMsg={formik.errors.description}
          isSpace={true}
          blockHtmlTags={true}
        />
      </div>

      {/* Start / End */}
      <div className="grid gap-4">
        <div className="grid grid-cols-4 gap-4">
          <div className="col-span-1">
            <Label htmlFor="startDate">Start date</Label>
            <CustomDatePicker
              id="startDate"
              variant="lg"
              error={formik.errors.startDate && formik.touched.startDate}
              errorMsg={formik.errors.startDate}
              value={formik.values.startDate}
              name="startDate"
              onChange={handleStartDateChange}
              onBlur={formik.handleBlur}
              placeholder="Select date"
              className="!py-0 w-full mt-2"
              minDate={startDateMin}
            />
          </div>
          <div className="col-span-1">
            <Label htmlFor="startTime">Start time</Label>
            <CustomTimePicker
              id="startTime"
              variant="lg"
              name="startTime"
              value={formik.values.startTime}
              onChange={handleStartTimeChange}
              onBlur={() => formik.setFieldTouched("startTime", true)}
              error={formik.touched.startTime && !!formik.errors.startTime}
              errorMsg={formik.errors.startTime}
              ampm={true}
              className="!py-0 w-full mt-2"
              minTime={startTimeMin || undefined}
              placeholder={{
                hours: "hh",
                minutes: "mm",
                meridiem: "AM/PM",
              }}
            />
          </div>

          <div className="col-span-1">
            <Label htmlFor="endDate">End date</Label>
            <CustomDatePicker
              id="endDate"
              variant="lg"
              error={formik.errors.endDate && formik.touched.endDate}
              errorMsg={formik.errors.endDate}
              value={formik.values.endDate}
              name="endDate"
              onChange={handleEndDateChange}
              onBlur={formik.handleBlur}
              placeholder="Select date"
              className="!py-0 w-full mt-2"
              minDate={endDateMin}
            />
          </div>
          <div className="col-span-1">
            <Label htmlFor="endTime">End time</Label>
            <CustomTimePicker
              id="endTime"
              variant="lg"
              name="endTime"
              value={formik.values.endTime}
              onChange={handleEndTimeChange}
              onBlur={() => formik.setFieldTouched("endTime", true)}
              error={formik.touched.endTime && !!formik.errors.endTime}
              errorMsg={formik.errors.endTime}
              ampm={true}
              className="!py-0 w-full mt-2"
              minTime={endTimeMin || undefined}
              placeholder={{
                hours: "hh",
                minutes: "mm",
                meridiem: "AM/PM",
              }}
            />
          </div>
        </div>
      </div>

      {/* Platforms */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <Label>Platforms</Label>

        </div>
        <div className="flex flex-col gap-1">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {PLATFORMS.map((p) => (
              <PlatformCard
                key={p.key}
                platform={p}
                enabled={formik.values.platforms[p.key]}
                onToggle={(checked) =>
                  formik.setFieldValue("platforms", {
                    ...formik.values.platforms,
                    [p.key]: checked,
                  })
                }
              />
            ))}
          </div>
          {formik.touched.platforms && formik.errors.platforms && (
            <span className="text-[13px] text-red-500">
              {formik.errors.platforms}
            </span>
          )}
        </div>
        {activePlatforms.length > 0 && <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Selected:</span>
          {activePlatforms.length === 0 ? (
            <span className="text-red-600 text-xs">None</span>
          ) : (
            activePlatforms.map((p) => (
              <span
                key={p.key}
                className="inline-flex items-center rounded-md border border-slate-200 px-2 py-0.5 text-xs font-medium"
              >
                {p.name}
              </span>
            ))
          )}
        </div>}

      </div>

      {/* Notification options */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex items-center justify-between rounded-xl border border-dashed bg-muted/40 p-3.5">
          <div>
            <div className="text-sm font-semibold text-foreground">
              Send email
            </div>
            <div className="text-xs text-muted-foreground">
              Notify users by email about this window.
            </div>
          </div>
          <VisiblitySwitch
            checked={formik.values.sendEmail}
            onChange={() =>
              formik.setFieldValue("sendEmail", !formik.values.sendEmail)
            }
          />
        </div>
        <div className="flex items-center justify-between rounded-xl border border-dashed bg-muted/40 p-3.5">
          <div>
            <div className="text-sm font-semibold text-foreground">
              Send notification
            </div>
            <div className="text-xs text-muted-foreground">
              Push an in-app notification to users.
            </div>
          </div>
          <VisiblitySwitch
            checked={formik.values.sendNotification}
            onChange={() =>
              formik.setFieldValue(
                "sendNotification",
                !formik.values.sendNotification,
              )
            }
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-1">
        <Button
          type="button"
          variant="outline"
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <SubmitLoaderButton
          btnText={row ? "Update" : "Add"}
          isDisable={formik.isSubmitting && !formError}
        />
      </div>
    </form>
  );
}

export default ModalEditMaintenance;